import { Link, useInRouterContext, useLocation, MemoryRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuthLite } from "@/context/AuthLite";
import { queryKeys } from "@/lib/query-keys";
import { fetchShoppingList } from "@/lib/shopping-list-api";
import {
  LayoutDashboard,
  Moon,
  PieChart,
  FileText,
  Settings,
  Sun,
  MapPin,
  Images,
  Users,
  ShoppingCart,
  ScrollText,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  isContactsActive,
  isCrmActive,
  isDashboardActive,
  isImagesActive,
} from "@/lib/nav-active";
import { useTheme } from "./ThemeProvider";

interface NavItem {
  icon: React.ReactNode;
  labelKey: string;
  to: string;
  check?: (pathname: string) => boolean;
}

interface SidebarProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function SidebarInner({ open, onOpenChange }: SidebarProps = {}) {
  const { pathname } = useLocation();
  const { user } = useAuthLite();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { setTheme, resolvedTheme } = useTheme();

  const { data: shoppingData } = useQuery({
    queryKey: queryKeys.shoppingList,
    queryFn: fetchShoppingList,
    staleTime: 60 * 1000,
    retry: false,
    enabled: !!user,
  });
  const shoppingPendingTotal = shoppingData?.pending_total ?? 0;

  const navItems: NavItem[] = [
    {
      icon: <PieChart className="w-5 h-5" />,
      labelKey: "sidebar.dashboard",
      to: "/dashboard",
      check: isDashboardActive,
    },
    {
      icon: <LayoutDashboard className="w-5 h-5" />,
      labelKey: "sidebar.crm",
      to: "/crm",
      check: isCrmActive,
    },
    {
      icon: <Users className="w-5 h-5" />,
      labelKey: "sidebar.contacts",
      to: "/contacts",
      check: isContactsActive,
    },
    {
      icon: <FileText className="w-5 h-5" />,
      labelKey: "sidebar.manuals",
      to: "/manuals",
    },
    {
      icon: <Images className="w-5 h-5" />,
      labelKey: "sidebar.images",
      to: "/images/gallery",
      check: isImagesActive,
    },
    {
      icon: <ShoppingCart className="w-5 h-5" />,
      labelKey: "sidebar.shoppingList",
      to: "/shopping-list",
      check: (p) => p === "/shopping-list",
    },
    ...(!isMobile
      ? [
          {
            icon: <MapPin className="w-5 h-5" />,
            labelKey: "sidebar.routeGenerator",
            to: "/admin/route-generator",
            check: (p: string) => p.startsWith("/admin/route-generator"),
          } satisfies NavItem,
        ]
      : []),
    {
      icon: <ScrollText className="w-5 h-5" />,
      labelKey: "sidebar.configuratorLog",
      to: "/admin/price-generator/log",
      check: (p) => p.startsWith("/admin/price-generator/log"),
    },
  ];

  const isActive = (item: NavItem) => {
    if (item.check) return item.check(pathname);
    return pathname === item.to || pathname.startsWith(item.to + "/");
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground">
      {/* Logo */}
      <Link
        to="/crm"
        onClick={() => isMobile && onOpenChange?.(false)}
        className={`${isMobile ? 'p-6' : 'mb-8'} flex items-center ${isMobile ? 'justify-start gap-3' : 'justify-center'} w-full ${isMobile ? 'h-16' : 'w-12 h-12'} rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground transition-colors ${isMobile ? 'mx-4 safe-area-top' : 'mx-auto'} p-2`}
      >
        <img 
          src="/tpt-logo.png" 
          alt={t('common.brandName')} 
          className="h-6 w-auto object-contain"
          onError={(e) => {
            console.error('Logo failed to load:', e);
            // Fallback to SVG if PNG fails
            const target = e.target as HTMLImageElement;
            if (target.src !== '/tpt-logo.svg') {
              target.src = '/tpt-logo.svg';
            }
          }}
        />
        {isMobile && <span className="font-semibold text-lg">{t('common.brandName')}</span>}
      </Link>

      {/* Navigation Items */}
      <nav className={`flex-1 ${isMobile ? 'px-4 py-6 space-y-2' : 'space-y-3 flex flex-col items-center'}`}>
        {user &&
          navItems.map((item) => {
            const active = isActive(item);
            const showShoppingBadge =
              item.to === "/shopping-list" && shoppingPendingTotal > 0;
            const linkContent = (
              <Link
                to={item.to}
                onClick={() => isMobile && onOpenChange?.(false)}
                className={`${isMobile 
                  ? `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${active ? "bg-primary text-primary-foreground" : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"}`
                  : `flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${active ? "bg-primary text-primary-foreground" : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"}`
                }`}
              >
                {showShoppingBadge ? (
                  <span className="relative inline-flex shrink-0">
                    {item.icon}
                    <span
                      className="absolute -top-1 -right-2 min-w-[1rem] h-4 px-1 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold"
                      aria-hidden
                    >
                      {shoppingPendingTotal > 99 ? "99+" : shoppingPendingTotal}
                    </span>
                  </span>
                ) : (
                  item.icon
                )}
                {isMobile && <span className="font-medium">{t(item.labelKey)}</span>}
              </Link>
            );

            if (isMobile) {
              return <div key={item.to}>{linkContent}</div>;
            }

            return (
              <Tooltip key={item.to} delayDuration={300}>
                <TooltipTrigger asChild>
                  {linkContent}
                </TooltipTrigger>
                <TooltipContent side="right" className="ml-2">
                  {t(item.labelKey)}
                </TooltipContent>
              </Tooltip>
            );
          })}
      </nav>

      {/* Bottom Controls */}
      <div className={`${isMobile ? 'px-4 pb-6' : 'space-y-3 flex flex-col items-center'}`}>
        {user && !isMobile && (
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                className="flex items-center justify-center w-12 h-12 rounded-lg transition-colors text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                aria-label={t('header.toggleTheme')}
              >
                {resolvedTheme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="ml-2">
              {t('header.toggleTheme')}
            </TooltipContent>
          </Tooltip>
        )}
        {isMobile ? (
          <Link
            to="/settings"
            onClick={() => onOpenChange?.(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              pathname.startsWith("/settings")
                ? "bg-primary text-primary-foreground"
                : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
            }`}
          >
            <Settings className="w-5 h-5" />
            <span className="font-medium">{t("sidebar.settings")}</span>
          </Link>
        ) : (
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <Link
                to="/settings"
                className={`flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${
                  pathname.startsWith("/settings")
                    ? "bg-primary text-primary-foreground"
                    : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                }`}
              >
                <Settings className="w-5 h-5" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" className="ml-2">
              {t("sidebar.settings")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="left"
          hideClose
          className="w-[60vw] max-w-[60vw] max-[767px]:w-[60vw] max-[767px]:max-w-[60vw] p-0 bg-sidebar border-r border-sidebar-border safe-area-top"
        >
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <TooltipProvider>
      <aside className="fixed hidden md:flex flex-col items-center w-20 bg-sidebar border-r border-sidebar-border min-h-screen left-0 top-0 bottom-0 z-50 py-4">
        {sidebarContent}
      </aside>
    </TooltipProvider>
  );
}

export default function Sidebar(props: SidebarProps = {}) {
  const inRouter = useInRouterContext();

  if (!inRouter) {
    return (
      <MemoryRouter>
        <SidebarInner {...props} />
      </MemoryRouter>
    );
  }

  return <SidebarInner {...props} />;
}
